// The event is in Vienna. Session datetimes are stored with the conference offset
// (...+02:00), and we ALWAYS format them with timeZone: "Europe/Vienna" so every
// attendee, wherever their device clock is set, sees the official schedule time: a
// 09:00+02:00 session reads "09:00" in Tokyo and in New York. The Intl.DateTimeFormat
// instances are relatively expensive, so they are created once and reused.

const TIME_ZONE = "Europe/Vienna";

const timeFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const dateFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: TIME_ZONE,
  weekday: "short",
  day: "numeric",
  month: "short",
});

const monthDayFormat = new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, month: "short", day: "numeric" });

const dayNumberFormat = new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, day: "numeric" });

const monthNameFormat = new Intl.DateTimeFormat("en-GB", { timeZone: TIME_ZONE, month: "short" });

// en-CA renders `YYYY-MM-DD` exactly, so the string sorts and compares
// lexicographically as a real date key.
const dayKeyFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function formatTime(iso) {
  return timeFormat.format(new Date(iso));
}

export function formatTimeRange(startIso, endIso) {
  return `${formatTime(startIso)} – ${formatTime(endIso)}`;
}

export function formatDate(iso) {
  return dateFormat.format(new Date(iso));
}

// The conference-local calendar date of an instant, as a `YYYY-MM-DD` key. Because it
// is evaluated in the conference time zone, two instants on the same conference day
// share a key wherever the viewer's device clock is set: the basis for "which
// conference day is today" and "is a session's day in the past". The `YYYY-MM-DD` shape
// makes a plain string comparison a valid date comparison.
//
// TOTAL by design: an unparseable input yields "" rather than throwing, so the timeline
// selectors (and the best-effort open-on-today at load time) can never turn a malformed
// session into a thrown error. "" sorts below every real key and matches none, so it
// drops out of any day mapping.
export function dayKey(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : dayKeyFormat.format(date);
}

// Compact Vienna-local day range for the hero eyebrow: a same-month span collapses to
// "15–17 Sept", a cross-month span reads "30 Sept – 2 Oct".
export function formatDayRange(startIso, endIso) {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (monthNameFormat.format(start) === monthNameFormat.format(end)) {
    return `${dayNumberFormat.format(start)}–${monthDayFormat.format(end)}`;
  }
  return `${monthDayFormat.format(start)} – ${monthDayFormat.format(end)}`;
}
